import type { ReactNode } from "react";
import { markerPaletteEntry } from "../../components/charts/markerPalette";
import { NO_RESIN_CODE, type PricePointFilters } from "./priceContextChart";

interface Props {
  priceTypes: string[];
  resinCodes: string[];
  filters: PricePointFilters;
  onChange: (filters: PricePointFilters) => void;
}

interface FilterChipProps {
  label: string;
  isSelected: boolean;
  swatchClassName?: string;
  onSelect: () => void;
}

function FilterChip({ label, isSelected, swatchClassName, onSelect }: FilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={`inline-flex items-center gap-1.5 bg-transparent border rounded-full px-2.5 py-0.5 text-xs cursor-pointer ${
        isSelected
          ? "border-rs-green text-rs-green"
          : "border-rs-jet text-rs-light-gray hover:border-rs-green hover:text-rs-green"
      }`}
      onClick={onSelect}
    >
      {swatchClassName && <span className={`inline-block w-2 h-2 rounded-full ${swatchClassName}`} aria-hidden="true" />}
      {label}
    </button>
  );
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      <span className="text-xs text-rs-light-gray w-20">{label}</span>
      {children}
    </div>
  );
}

export function PricePointFilterBar({ priceTypes, resinCodes, filters, onChange }: Props) {
  const showPriceTypes = priceTypes.length > 1;
  const showResinCodes = resinCodes.length > 1;
  if (!showPriceTypes && !showResinCodes) return null;

  return (
    <div className="flex flex-col gap-1.5 mb-3">
      {showPriceTypes && (
        <FilterRow label="Price type">
          <FilterChip
            label="All"
            isSelected={filters.priceTypeCode === null}
            onSelect={() => onChange({ ...filters, priceTypeCode: null })}
          />
          {priceTypes.map((priceType, priceTypeIndex) => (
            <FilterChip
              key={priceType}
              label={priceType}
              swatchClassName={markerPaletteEntry(priceTypeIndex).swatchClassName}
              isSelected={filters.priceTypeCode === priceType}
              onSelect={() => onChange({ ...filters, priceTypeCode: priceType })}
            />
          ))}
        </FilterRow>
      )}
      {showResinCodes && (
        <FilterRow label="Resin code">
          <FilterChip
            label="All"
            isSelected={filters.resinCode === null}
            onSelect={() => onChange({ ...filters, resinCode: null })}
          />
          {resinCodes.map((resinCode) => (
            <FilterChip
              key={resinCode}
              label={resinCode === NO_RESIN_CODE ? "No resin code" : resinCode}
              isSelected={filters.resinCode === resinCode}
              onSelect={() => onChange({ ...filters, resinCode })}
            />
          ))}
        </FilterRow>
      )}
    </div>
  );
}
